import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ArtifactLink as ArtifactLinkTo } from '../components/ArtifactLink';
import {
  ADDON_UPLOAD_KIND_LABELS,
  ARTIFACT_TYPE_LABELS,
  type AddonUploadSource,
  type AddonUploadView,
  type ArtifactType,
  type BuildView,
  type SubmissionView,
  pageTitle,
} from '@thottbot/shared';
import { apiGet, useMe } from '../api/client';
import { LoginGate } from '../components/LoginGate';
import { MyCharacters } from '../features/characters/MyCharacters';
import { relativeTime } from '../util/time';

interface ArtifactLink {
  type: ArtifactType;
  slug: string;
  name: string;
  status: string;
}
interface Contributions {
  builds: BuildView[];
  submissions: (SubmissionView & { artifact: ArtifactLink | null })[];
  screenshots: {
    id: string;
    url: string;
    caption: string | null;
    removed: boolean;
    createdAt: string;
    artifact: ArtifactLink | null;
  }[];
  comments: {
    id: string;
    body: string;
    score: number;
    removed: boolean;
    createdAt: string;
    artifact: ArtifactLink | null;
  }[];
  addonUploads: AddonUploadView[];
}

const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' } as const;
/** How an addon upload reached the site, as the contributions and admin pages word it. */
export function sourceLabel(source: AddonUploadSource): string {
  return source === 'watcher' ? 'automatic' : 'manual';
}

function ArtLink({ a, fallback }: { a: ArtifactLink | null; fallback: string }) {
  if (!a) return <span>{fallback}</span>;
  if (a.status !== 'published') return <span className="muted">{a.name} (removed)</span>;
  return <ArtifactLinkTo type={a.type} slug={a.slug} name={a.name} />;
}

export function Me() {
  const me = useMe();
  const q = useQuery({
    queryKey: ['me', 'contributions'],
    queryFn: () => apiGet<Contributions>('/api/me/contributions'),
    enabled: !!me.data?.user,
  });
  return (
    <div className="page">
      <title>{pageTitle('My contributions')}</title>
      <h1>My contributions</h1>
      <LoginGate action="see your contributions">
        <MyCharacters />
        {q.isPending && <p className="muted">Loading...</p>}
        {q.data && (
          <>
            <section className="section" data-testid="my-submissions">
              <h2>Submissions</h2>
              {q.data.submissions.length === 0 ? (
                <p className="muted">
                  Nothing yet. <Link to="/submit">Submit an entry</Link> or edit one you know about.
                </p>
              ) : (
                <div className="table-scroll">
                  <table className="grid">
                    <thead>
                      <tr>
                        <th>Entry</th>
                        <th>Kind</th>
                        <th>Status</th>
                        <th>When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.data.submissions.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <ArtLink a={s.artifact} fallback={s.proposedName} />{' '}
                            <span className="muted">({ARTIFACT_TYPE_LABELS[s.type].singular})</span>
                          </td>
                          <td>{s.kind === 'create' ? 'New entry' : 'Edit'}</td>
                          <td>
                            {STATUS_LABEL[s.status]}
                            {s.status === 'rejected' && s.rejectReason && (
                              <span className="muted"> - {s.rejectReason}</span>
                            )}
                          </td>
                          <td>{relativeTime(s.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <section className="section" data-testid="my-builds">
              <h2>Builds</h2>
              {q.data.builds.length === 0 ? (
                <p className="muted">
                  Nothing yet. <Link to="/build">Generate a build</Link> for one of your characters.
                </p>
              ) : (
                <div className="table-scroll">
                  <table className="grid">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Character</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.data.builds.map((b) => (
                        <tr key={b.id} data-testid="my-build" data-status={b.status}>
                          <td>{relativeTime(b.createdAt)}</td>
                          <td>
                            {b.character.characterName ?? `Level ${b.character.level} ${b.character.race} ${b.character.class}`} &mdash;{' '}
                            {b.character.role}
                          </td>
                          <td>{b.status}</td>
                          <td>
                            <Link to={`/build/${b.id}`}>View</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <section className="section" data-testid="my-addon-uploads">
              <h2>Addon uploads</h2>
              {q.data.addonUploads.length === 0 ? (
                <p className="muted">
                  Nothing yet. Install the <Link to="/addon">Thottbot addon</Link> and{' '}
                  <Link to="/addon/upload">upload its file</Link> after playing, or{' '}
                  <Link to="/addon/watch">let the site upload it for you</Link>.
                </p>
              ) : (
                <div className="table-scroll">
                  <table className="grid">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>File</th>
                        <th>Version</th>
                        <th>Build</th>
                        <th>How</th>
                        <th>Status</th>
                        <th>Artifacts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.data.addonUploads.map((u) => (
                        <tr
                          key={u.id}
                          data-testid="my-addon-upload"
                          data-status={u.status}
                          data-kind={u.kind}
                        >
                          <td>{relativeTime(u.createdAt)}</td>
                          <td data-testid="my-addon-upload-kind">
                            {ADDON_UPLOAD_KIND_LABELS[u.kind]}
                          </td>
                          <td>{u.addonVersion}</td>
                          <td>{u.clientBuild}</td>
                          <td data-testid="my-addon-upload-source">{sourceLabel(u.source)}</td>
                          <td>
                            {u.status}
                            {u.error && <span className="muted"> - {u.error}</span>}
                          </td>
                          <td>
                            {u.counts.artifacts
                              ? `${u.counts.artifacts.added} added, ${u.counts.artifacts.updated} updated`
                              : `${u.counts.read.npcs} NPCs, ${u.counts.read.quests} quests, ${u.counts.read.items} items read`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <section className="section">
              <h2>Screenshots</h2>
              {q.data.screenshots.length === 0 ? (
                <p className="muted">No screenshots yet.</p>
              ) : (
                <ul>
                  {q.data.screenshots.map((s) => (
                    <li key={s.id}>
                      <ArtLink a={s.artifact} fallback="(removed entry)" />{' '}
                      {s.caption && <span>- {s.caption}</span>}{' '}
                      {s.removed && <span className="muted">(removed)</span>}{' '}
                      <span className="muted">{relativeTime(s.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="section">
              <h2>Comments</h2>
              {q.data.comments.length === 0 ? (
                <p className="muted">No comments yet.</p>
              ) : (
                <ul>
                  {q.data.comments.map((c) => (
                    <li key={c.id}>
                      <ArtLink a={c.artifact} fallback="(removed entry)" />: {c.body.slice(0, 120)}
                      {c.body.length > 120 && '...'}{' '}
                      <span className="muted">
                        (score {c.score}
                        {c.removed ? ', removed' : ''})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </LoginGate>
    </div>
  );
}
